/**
 * harmony-reviews — review-card carousel + home scroll row.
 *
 * Initialises the vendored Embla (`window.EmblaCarousel`, loaded defer before
 * this file) on every `[data-h-reviews-viewport]` inside a `[data-h-reviews]`
 * root, and wires the `[data-h-reviews-prev]` / `[data-h-reviews-next]` pill
 * buttons to `scrollPrev` / `scrollNext`.
 *
 * The carousel does not loop, so each edge is a real stop: the desktop pills
 * hide at their dead end (prev at the start, next at the end) — Figma draws
 * them at opacity 0 there. The `md:`-prefixed classes keep them visible at
 * all times on mobile, where both pills sit below the cards.
 *
 * A horizontal trackpad swipe or horizontal wheel steps the carousel too
 * (`bindWheel`, same helper as `harmony-product.js`): Embla ignores wheel
 * events on its own.
 *
 * The home layout (`layout: 'home'`, Figma 1307:7182 / 1312:11390) needs no
 * carousel library: its cards sit in a native scroll-snap row,
 * `[data-h-reviews-home-viewport]` inside a `[data-h-reviews-home]` root, and
 * the mobile `[data-h-reviews-home-prev]` / `[data-h-reviews-home-next]` pills
 * scroll it by one card width via `scrollBy`. This part runs without Embla.
 */
(function () {
  'use strict';

  var HIDDEN_CLASSES = ['md:opacity-0', 'md:invisible'];

  /* Embla only drags with pointer and touch, and its viewport is
     overflow-hidden, so a two-finger trackpad swipe did nothing. A mostly
     horizontal wheel gesture now steps one snap; the rest of that gesture
     (trackpad inertia included) is swallowed until the wheel has been quiet
     for WHEEL_IDLE ms. Vertical gestures keep scrolling the page. */
  var WHEEL_THRESHOLD = 20;
  var WHEEL_IDLE = 200;

  function bindWheel(embla) {
    var travel = 0;
    var stepped = false;
    var idleTimer;
    embla.rootNode().addEventListener(
      'wheel',
      function (event) {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        if (!embla.canScrollPrev() && !embla.canScrollNext()) return;
        event.preventDefault();
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function () {
          travel = 0;
          stepped = false;
        }, WHEEL_IDLE);
        if (stepped) return;
        travel += event.deltaMode === 1 ? event.deltaX * 16 : event.deltaX;
        if (Math.abs(travel) < WHEEL_THRESHOLD) return;
        stepped = true;
        if (travel > 0) {
          embla.scrollNext();
        } else {
          embla.scrollPrev();
        }
      },
      { passive: false }
    );
  }

  function initReviews(root) {
    var viewport = root.querySelector('[data-h-reviews-viewport]');
    if (!viewport || viewport.hasAttribute('data-h-reviews-init')) return;
    viewport.setAttribute('data-h-reviews-init', '');

    var embla = window.EmblaCarousel(viewport, { align: 'start' });
    bindWheel(embla);
    var prev = root.querySelector('[data-h-reviews-prev]');
    var next = root.querySelector('[data-h-reviews-next]');

    if (prev) {
      prev.addEventListener('click', function () {
        embla.scrollPrev();
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        embla.scrollNext();
      });
    }

    var updateArrows = function () {
      [prev, next].forEach(function (button, index) {
        if (!button) return;
        var deadEnd = index === 0 ? !embla.canScrollPrev() : !embla.canScrollNext();
        HIDDEN_CLASSES.forEach(function (className) {
          button.classList.toggle(className, deadEnd);
        });
      });
    };
    embla.on('select', updateArrows);
    embla.on('reInit', updateArrows);
    updateArrows();
  }

  function initHome(root) {
    var viewport = root.querySelector('[data-h-reviews-home-viewport]');
    if (!viewport || viewport.hasAttribute('data-h-reviews-home-init')) return;
    viewport.setAttribute('data-h-reviews-home-init', '');

    var prev = root.querySelector('[data-h-reviews-home-prev]');
    var next = root.querySelector('[data-h-reviews-home-next]');

    var step = function () {
      var card = viewport.querySelector('[data-h-reviews-home-card]');
      // Card width plus the gap-h-2 (8px) between slides.
      return card ? card.getBoundingClientRect().width + 8 : viewport.clientWidth;
    };

    if (prev) {
      prev.addEventListener('click', function () {
        viewport.scrollBy({ left: -step(), behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        viewport.scrollBy({ left: step(), behavior: 'smooth' });
      });
    }
  }

  function init() {
    if (typeof window.EmblaCarousel === 'function') {
      document.querySelectorAll('[data-h-reviews]').forEach(initReviews);
    }
    document.querySelectorAll('[data-h-reviews-home]').forEach(initHome);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
